import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { deauthenticate } from '../store/api/apiSlice';
import { useGetProfileDetailsQuery } from '../store/api/profileApi';
import { selectUser } from '../store/slices/authSlice';
import { t } from '../i18n/i18n';
import './CompleteProfileDialog.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The checklist, in the order Profile → My Profile lays its fields out. */
const ITEMS = [
  { field: 'first_name', label: 'First name' },
  { field: 'last_name', label: 'Last name' },
  { field: 'contact_number', label: 'Contact number' },
  { field: 'country', label: 'Country' },
  { field: 'city', label: 'City' },
  { field: 'date_of_birth', label: 'Date of birth' },
];

/**
 * Whether the record answers a field — the same test Profile uses to lock it.
 * The backend sends an unset contact number as `0`, so that counts as empty.
 */
const isFilled = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim();
  return str !== '' && str !== '0';
};

/** Where the details are filled in — and where this steps aside. */
const PROFILE = '/profile';

/**
 * "Complete your profile" — which of the trader record's details the backend
 * has no answer for yet (`GET /v3/user/profile/details/`), as a checklist. It
 * only informs: the details are filled in on Profile, where its one button goes.
 *
 * It can't be dismissed: no close button, Escape or mask click. On Profile it
 * steps aside so the form there can be used, and comes back on any other page
 * until the record is complete — the save's `Profile` tag refetch tells it.
 *
 * Mounted once in the shell. It waits for the record to load: a failed or
 * pending read is no reason to stop anyone.
 */
export default function CompleteProfileDialog() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector(selectUser);

  const { data, isSuccess } = useGetProfileDetailsQuery(undefined, { skip: !user });

  const missing = isSuccess ? ITEMS.filter((item) => !isFilled(data?.[item.field])) : [];
  const onProfile = pathname === PROFILE || pathname.startsWith(`${PROFILE}/`);
  const open = missing.length > 0 && !onProfile;

  return (
    <Modal
      open={open}
      // No close button, Escape or mask click: the record has to be complete
      // first, on Profile.
      footer={null}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      centered
      width={420}
      className="profile-gate"
      rootClassName="profile-gate-root"
      aria-label={t('Complete your profile')}
    >
      <div className="profile-gate__body">
        <header className="profile-gate__head">
          <span className="profile-gate__glyph" aria-hidden="true">
            <span className="icon" style={icon('profile-user')} />
          </span>
          <h2 className="profile-gate__title">{t('Complete your profile')}</h2>
        </header>

        <p className="profile-gate__alert" role="alert">
          {t(
            'Some details are missing from your account. Please add them to continue. We use them to verify your identity and process payouts.',
          )}
        </p>

        <ul className="profile-gate__list">
          {ITEMS.map((item) => {
            const isDone = !missing.includes(item);
            return (
              <li key={item.field} className={`profile-gate__item${isDone ? ' is-done' : ''}`}>
                <span className="profile-gate__mark" aria-hidden="true">
                  {isDone && <span className="icon" style={icon('check-sm')} />}
                </span>
                <span className="profile-gate__label">{t(item.label)}</span>
                <span className="profile-gate__state">{isDone ? t('Added') : t('Missing')}</span>
              </li>
            );
          })}
        </ul>

        <button type="button" className="profile-gate__primary" onClick={() => navigate(PROFILE)}>
          {t('Complete profile')}
        </button>

        <button type="button" className="profile-gate__signout" onClick={() => dispatch(deauthenticate())}>
          {t('Not you? Log out')}
        </button>
      </div>
    </Modal>
  );
}
